import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Phone, Search } from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import { cn } from '../../../utils/tailwindUtils';
import ReportFilters, { presetToRange } from '../reports/ReportFilters';
import {
  ENGAGE_AI_CALLER, OUTCOME_BADGE, STATUS_BADGE,
  formatDuration, prettyOutcome, prettyStatus, LANGUAGE_LABEL, formatInr,
} from './aiCallerConstants';
import CallCostBreakdown from './CallCostBreakdown';


const OUTCOME_OPTIONS = [
  { value: 'all',                label: 'All outcomes' },
  { value: 'demo_booked',        label: 'Demo booked' },
  { value: 'qualified',          label: 'Qualified' },
  { value: 'callback_requested', label: 'Callback requested' },
  { value: 'not_interested',     label: 'Not interested' },
  { value: 'disqualified',       label: 'Disqualified' },
  { value: 'no_answer',          label: 'No answer' },
  { value: 'error',              label: 'Error' },
];

const STATUS_OPTIONS = [
  { value: 'all',         label: 'All statuses' },
  { value: 'queued',      label: 'Queued' },
  { value: 'ringing',     label: 'Ringing' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed',   label: 'Completed' },
  { value: 'failed',      label: 'Failed' },
  { value: 'no_answer',   label: 'No answer' },
];


export default function EngageAICallerCallLogs() {
  const navigate = useNavigate();
  const { fetch: apiFetch } = useApiMethods();
  const [searchParams] = useSearchParams();
  const enquiryIdParam = searchParams.get('enquiry_id') || '';

  const [calls, setCalls] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // When landing from an enquiry timeline, show all history for that enquiry.
  const [preset, setPreset] = useState(enquiryIdParam ? 'all' : 'last_30');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [outcome, setOutcome] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [reloadTick, setReloadTick] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 25;
  const [costModalCall, setCostModalCall] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { from, to } = presetToRange(preset, customFrom, customTo);
      const q = new URLSearchParams({ page, limit });
      if (from) q.set('date_from', from);
      if (to)   q.set('date_to', to);
      if (outcome !== 'all')      q.set('outcome', outcome);
      if (statusFilter !== 'all') q.set('status', statusFilter);
      if (enquiryIdParam)         q.set('enquiry_id', enquiryIdParam);
      const resp = await apiFetch(`${ENGAGE_AI_CALLER.CALLS_LIST}?${q.toString()}`);
      setCalls(resp?.data || resp?.data?.data || []);
      setTotalCount(resp?.total_count ?? resp?.data?.total_count ?? 0);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load calls');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, customFrom, customTo, outcome, statusFilter, page, reloadTick, enquiryIdParam]);

  useEffect(() => { load(); }, [load]);

  const rows = search.trim()
    ? calls.filter(c =>
        (c.enquiry_name  || '').toLowerCase().includes(search.trim().toLowerCase())
     || (c.enquiry_mobile || '').includes(search.trim())
      )
    : calls;

  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  return (
    <div className="p-4 max-w-[1400px] mx-auto space-y-4">
      <button onClick={() => navigate('/engage/ai-caller/dashboard')} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to AI Voice Agent
      </button>

      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white shadow-sm">
          <Phone className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Call Logs</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Every AI call across your enquiries</p>
        </div>
      </div>

      <ReportFilters
        preset={preset} setPreset={setPreset}
        customFrom={customFrom} setCustomFrom={setCustomFrom}
        customTo={customTo} setCustomTo={setCustomTo}
        onReload={() => setReloadTick(t => t + 1)}
        extras={[
          { label: 'Outcome', value: outcome,      onChange: v => { setOutcome(v); setPage(1); },      options: OUTCOME_OPTIONS },
          { label: 'Status',  value: statusFilter, onChange: v => { setStatusFilter(v); setPage(1); }, options: STATUS_OPTIONS },
        ]}
      />

      <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or mobile…"
            className="w-full pl-10 pr-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500" />
        </div>
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Enquiry</th>
              <th className="px-4 py-2 text-left font-medium">Mobile</th>
              <th className="px-4 py-2 text-left font-medium">Status</th>
              <th className="px-4 py-2 text-left font-medium">Outcome</th>
              <th className="px-4 py-2 text-right font-medium">Duration</th>
              <th className="px-4 py-2 text-right font-medium">Cost</th>
              <th className="px-4 py-2 text-left font-medium">Language</th>
              <th className="px-4 py-2 text-right font-medium">Started</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading ? <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">Loading…</td></tr>
            : rows.length === 0 ? <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">No calls match filters.</td></tr>
            : rows.map(c => (
              <tr key={c.id} onClick={() => navigate(`/engage/ai-caller/calls/${c.id}`)}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer">
                <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-white">
                  <div className="flex items-center gap-1.5">
                    {c.enquiry_name || '—'}
                    {c.requires_info_response && (
                      <span title="Patient asked for info — admin response pending" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60">
                        INFO
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">{c.enquiry_mobile || '—'}</td>
                <td className="px-4 py-2.5"><span title={c.error_message || undefined} className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', STATUS_BADGE[c.status] || 'bg-gray-100 text-gray-700', c.error_message && 'cursor-help')}>{prettyStatus(c.status)}</span></td>
                <td className="px-4 py-2.5">{c.outcome ? <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', OUTCOME_BADGE[c.outcome] || 'bg-gray-100 text-gray-700')}>{prettyOutcome(c.outcome)}</span> : '—'}</td>
                <td className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-300">{formatDuration(c.call_duration_seconds)}</td>
                <td
                  className="px-4 py-2.5 text-right font-medium text-teal-700 dark:text-teal-400 hover:underline"
                  onClick={(e) => { e.stopPropagation(); setCostModalCall(c); }}
                  title="Click for breakdown"
                >
                  {formatInr(c.total_cost_inr)}
                </td>
                <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">{LANGUAGE_LABEL[c.detected_language] || c.detected_language || '—'}</td>
                <td className="px-4 py-2.5 text-right text-[12px] text-gray-500 dark:text-gray-400">
                  {c.started_at ? new Date(c.started_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CallCostBreakdown
        open={!!costModalCall}
        onClose={() => setCostModalCall(null)}
        call={costModalCall}
      />

      {totalCount > limit && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Showing {rows.length} of {totalCount}</span>
          <div className="flex items-center gap-2">
            <button disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}
              className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 disabled:opacity-50">
              Prev
            </button>
            <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 disabled:opacity-50">
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
